// Module: MultiImageUploadBrick
// Role: Multi-image checkpoint upload: presign/upload each required image, submit once.
//       Required flags decide readiness; optional photos never block submit and
//       the payload carries only uploaded records (BUG-1593).
// Uses: lib/uploadTelemetry:createUploadFunnel+uploadTimeoutFor,
//       lib/imageResize:ImageResizeError,
//       screens/pantene/_uploadXHR:UploadTimeoutError,
//       multi-image-requirements:buildArtefacts+isReadyToSubmit+nextUploadIndex,
//       MultiImageUploadZone:MultiImageUploadZone
// Used by: brick-registry.ts for checkpoints with multiple image artefact requirements.
// FTR-1317: funnel telemetry replaces ad-hoc apiFetch beacons.

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PlatformContext } from '../../../providers/PlatformProvider';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useArtefactPresign, useSubmitCheckpoint } from '../../../api/flow-client';
import { MAX_PHOTO_INPUT_BYTES, FLOW_PHOTO_MAX_DIM } from '../../../shared/limits';
import { resizeImageToBlob, ImageResizeError } from '../../../lib/imageResize';
import { createUploadFunnel, uploadTimeoutFor } from '../../../lib/uploadTelemetry';
import { UploadTimeoutError } from '../../../screens/pantene/_uploadXHR';
import { submitErrorText } from '../submitErrorText';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { HintBar } from '../atoms/HintBar';
import { StepStrip } from '../StepStrip';
import { P, S, T } from '../../../styles/tokens';
import { flowStepLabel } from '../_labels';
import {
  buildArtefacts,
  imageRequirements,
  isReadyToSubmit,
  nextUploadIndex,
  roleFor,
} from './multi-image-requirements';
import { MultiImageUploadZone } from './MultiImageUploadZone';
import { splitInstructionText } from './instruction-text';
import type {
  Checkpoint,
  CheckpointAttempt,
  SlotFlowRun,
} from '../../../api/flow-types';

interface UploadedImage {
  artefactPath: string;
  thumbUrl: string;
}

export interface MultiImageUploadBrickProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

export function MultiImageUploadBrick({
  checkpoint,
  flowRun,
  checkpoints,
}: MultiImageUploadBrickProps) {
  const platform = useContext(PlatformContext);
  const canUpload = platform?.capabilities.fileUpload ?? false;

  const requirements = useMemo(() => imageRequirements(checkpoint), [checkpoint]);
  const [hintMessage, setHintMessage] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<Record<number, UploadedImage>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Preview shown immediately on pick, before presign+upload finish for that slot.
  const [pendingPreview, setPendingPreview] = useState<{ index: number; url: string } | null>(null);
  const uploadedRef = useRef<Record<number, UploadedImage>>({});
  const pendingPreviewRef = useRef<string | null>(null);
  const activeIndexRef = useRef(0);
  const submitInFlightRef = useRef(false);

  const upload = useFileUpload();
  const presign = useArtefactPresign(flowRun.slot_id);
  const submitCheckpoint = useSubmitCheckpoint(flowRun.slot_id, checkpoint.id);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const parentCount = checkpoints.filter((c) => c.parent_checkpoint_id === null).length;
  const instruction = splitInstructionText(
    checkpoint.instructions_text ?? 'Добавьте все обязательные файлы.',
  );
  const uploadedCount = requirements.filter((_, index) => Boolean(uploaded[index])).length;
  const isComplete = isReadyToSubmit(requirements, uploaded);
  const isUploading = upload.status === 'uploading';
  const isSubmitting = submitCheckpoint.isPending;

  useEffect(() => {
    uploadedRef.current = uploaded;
  }, [uploaded]);

  useEffect(
    () => () => {
      Object.values(uploadedRef.current).forEach((item) => URL.revokeObjectURL(item.thumbUrl));
      if (pendingPreviewRef.current !== null) URL.revokeObjectURL(pendingPreviewRef.current);
    },
    [],
  );

  const pickSlot = useCallback(
    (index: number) => {
      if (!canUpload) {
        setHintMessage('Загрузка файлов недоступна на этой платформе.');
        return;
      }
      if (isSubmitted || isUploading || isSubmitting) return;
      activeIndexRef.current = index;
      fileInputRef.current?.click();
    },
    [canUpload, isSubmitted, isUploading, isSubmitting],
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (fileInputRef.current) fileInputRef.current.value = '';

      // FTR-1317 follow-up: create the funnel BEFORE the early exits so the
      // previously-invisible pre-funnel failures (picker cancel, oversize) emit a row.
      const funnel = createUploadFunnel(flowRun.slot_id, checkpoint.slug);
      if (!file) {
        funnel.stage('picker_null');
        funnel.flush('abandoned');
        return;
      }

      const currentIndex = activeIndexRef.current;
      const req = requirements[currentIndex];
      if (!req) {
        funnel.stage('no_requirement');
        funnel.flush('failed');
        return;
      }

      // BUG-1304: 50 MB input guard
      const maxSize = req.max_size_bytes ?? MAX_PHOTO_INPUT_BYTES;
      if (file.size > maxSize) {
        setHintMessage('Файл слишком большой. Уменьши и попробуй снова.');
        funnel.stage('oversize', { size: file.size });
        funnel.flush('failed');
        return;
      }

      setHintMessage(null);

      funnel.stage('file_picked', { mime: file.type, size: file.size });

      // BUG-1304: normalize to JPEG
      let uploadFile = file;
      try {
        funnel.stage('resize_start');
        const blob = await resizeImageToBlob(file, { maxDim: FLOW_PHOTO_MAX_DIM, quality: 0.85 });
        uploadFile = new File([blob], 'upload.jpg', { type: 'image/jpeg' });
        funnel.stage('resize_done', { size: blob.size });
      } catch (resizeErr) {
        if (resizeErr instanceof ImageResizeError && resizeErr.code === 'decode_timeout') {
          funnel.stage('resize_timeout');
          funnel.flush('timeout');
          setHintMessage('Загрузка идёт дольше обычного — проверь интернет и попробуй ещё раз');
          return;
        }
        funnel.stage('resize_failed', { code: resizeErr instanceof ImageResizeError ? resizeErr.code : undefined });
        console.warn('[FTR-1317] re-encode failed, uploading original:', resizeErr);
      }

      const objectUrl = URL.createObjectURL(uploadFile);
      if (pendingPreviewRef.current !== null) URL.revokeObjectURL(pendingPreviewRef.current);
      pendingPreviewRef.current = objectUrl;
      setPendingPreview({ index: currentIndex, url: objectUrl });

      try {
        funnel.stage('presign_start');
        const presignResult = await presign.mutateAsync({
          checkpoint_slug: checkpoint.slug,
          artefact_kind: req.kind,
          content_md5: '',
          file_size_bytes: uploadFile.size,
        });
        funnel.stage('presign_done');

        funnel.stage('upload_start');
        await upload.start({ url: presignResult.upload_url, file: uploadFile, timeoutMs: uploadTimeoutFor(uploadFile.size) });
        funnel.stage('upload_done');

        funnel.flush('success');
        pendingPreviewRef.current = null;
        setUploaded((prev) => {
          const old = prev[currentIndex]?.thumbUrl;
          if (old) URL.revokeObjectURL(old);
          return {
            ...prev,
            [currentIndex]: {
              artefactPath: presignResult.artefact_path,
              thumbUrl: objectUrl,
            },
          };
        });
        setPendingPreview((p) => (p?.index === currentIndex ? null : p));
      } catch (err: unknown) {
        const isTimeout = err instanceof UploadTimeoutError || (err instanceof DOMException && err.name === 'AbortError');
        if (isTimeout) {
          funnel.stage('upload_timeout');
          funnel.flush('timeout');
          setHintMessage('Загрузка идёт дольше обычного — проверь интернет и попробуй ещё раз');
        } else {
          const msg = err instanceof Error ? err.message : 'Неизвестная ошибка';
          funnel.stage('upload_error');
          funnel.flush('failed');
          setHintMessage(`Ошибка загрузки: ${msg}`);
        }
        if (pendingPreviewRef.current === objectUrl) {
          URL.revokeObjectURL(objectUrl);
          pendingPreviewRef.current = null;
        }
        setPendingPreview((p) => (p?.index === currentIndex ? null : p));
      }
    },
    [checkpoint.slug, flowRun.slot_id, presign, requirements, upload],
  );

  const handleCta = useCallback(async () => {
    if (!isComplete) {
      const nextIndex = nextUploadIndex(requirements, uploaded);
      if (nextIndex >= 0) pickSlot(nextIndex);
      return;
    }
    if (isSubmitted || isSubmitting || submitInFlightRef.current) return;

    const artefacts = buildArtefacts(requirements, uploaded);

    submitInFlightRef.current = true;
    try {
      setHintMessage(null);
      await submitCheckpoint.mutateAsync({
        artefact_path: artefacts[0]?.artefact_path ?? null,
        artefacts,
      });
      setIsSubmitted(true);
      platform?.hapticImpact('medium');
    } catch (err: unknown) {
      setHintMessage(submitErrorText(err));
    } finally {
      submitInFlightRef.current = false;
    }
  }, [
    isComplete,
    isSubmitted,
    isSubmitting,
    pickSlot,
    platform,
    requirements,
    submitCheckpoint,
    uploaded,
  ]);

  const ctaLabel = isSubmitted
    ? 'Готово'
    : isComplete
      ? 'Отправить'
      : `Загрузить ${Math.min(uploadedCount + 1, requirements.length)} из ${requirements.length}`;

  return (
    <Frame>
      <MiniHeader title={checkpoint.display_name ?? flowStepLabel(checkpoint.kind)} />
      <div className="px-4 pt-3 pb-2">
        <StepStrip
          current={checkpoint.position + 1}
          total={parentCount}
          sub={{ current: uploadedCount, total: requirements.length }}
          showCaption
        />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
        <div style={{ paddingTop: 4 }}>
          <div style={{ font: T.title3, color: S.label, lineHeight: 1.2 }}>
            Загрузите фото и скриншоты
          </div>
          {instruction.intro ? (
            <div
              style={{
                font: T.footnote,
                color: S.secondaryLabel,
                marginTop: 6,
                lineHeight: 1.45,
                whiteSpace: 'pre-line',
              }}
            >
              {instruction.intro}
            </div>
          ) : null}
          {instruction.items.length > 0 ? (
            <div
              style={{
                marginTop: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {instruction.items.map((item, index) => (
                <div
                  key={`${item.number}-${index}`}
                  style={{ display: 'flex', gap: 6, font: T.footnote, lineHeight: 1.45 }}
                >
                  <span
                    style={{
                      color: S.label,
                      fontWeight: 600,
                      flexShrink: 0,
                      minWidth: 12,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {item.number})
                  </span>
                  <span style={{ color: S.label }}>{item.text}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <InstructionCard p={12}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {requirements.map((req, index) => {
              const item = uploaded[index];
              const done = Boolean(item);
              const pending = pendingPreview?.index === index ? pendingPreview.url : null;
              const thumb = item?.thumbUrl ?? pending;
              return (
                <MultiImageUploadZone
                  key={`${roleFor(req, index)}-${index}`}
                  req={req}
                  index={index}
                  thumbUrl={thumb}
                  done={done}
                  busy={pending !== null}
                  disabled={!canUpload || isSubmitted || isUploading || isSubmitting}
                  pickable={canUpload && !isSubmitted}
                  onPick={pickSlot}
                />
              );
            })}
          </div>

          {isUploading && upload.progress !== null && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  height: 4,
                  borderRadius: 2,
                  background: P.line,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.round((upload.progress ?? 0) * 100)}%`,
                    background: P.terracotta,
                    transition: 'width 0.2s linear',
                  }}
                />
              </div>
            </div>
          )}
        </InstructionCard>

        {!canUpload && <HintBar>Загрузка файлов недоступна на этой платформе.</HintBar>}
        {hintMessage && canUpload && <HintBar>{hintMessage}</HintBar>}
      </div>

      {canUpload && !isSubmitted && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          data-testid="multi-image-input"
          aria-hidden="true"
        />
      )}

      <WizardCtaBar
        label={ctaLabel}
        onClick={handleCta}
        disabled={!canUpload || isSubmitted || isUploading || isSubmitting}
      />
    </Frame>
  );
}
